import React, { useState, useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Typography, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import ActionMenu from './action-menu';
import CategoryNameCodeFilter from './filters/category-code-filter';
import TablePlaceholder from './table-placeholder';
import { useSettings } from '../providers/settings-provider';

type CategoryTableProps = {
    onAddSubCategory?: (parent: { code: string, id: number }) => void;
    onEditCategory?: (category: { id: number | null, parent: { code: string, id: number } | null }) => void;
    onDeleteCategory?: (categoryId: number) => void;
    categories: Category[];
    availableCodes?: string[];
    onSort?: (field: string, direction: 'asc' | 'desc') => void;
    onFilter?: (field: 'code' | 'name', value: string | string[]) => void;
    stage?: 'initialize' | 'fetching' | 'complete';
    canCreate?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
}

export type Category = {
    id: number;
	code: string;
	name: string;
	balance: string;
    searchStrings: string;
	children?: Category[];
	transactionCount?: number;
};

const CategoryTable: React.FC<CategoryTableProps> = ({ onAddSubCategory, onEditCategory, onDeleteCategory, categories, availableCodes, onSort, onFilter, stage = 'fetching', canCreate = true, canUpdate = true, canDelete = true }) => {
    const { formatAmount } = useSettings();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [expanded, setExpanded] = useState<{ [code: string]: boolean }>({});
    const [isCodeFilterOpen, setIsCodeFilterOpen] = useState(false);
    const [isNameFilterOpen, setIsNameFilterOpen] = useState(false);

    // Initialize all categories with children to be expanded by default
    React.useEffect(() => {
        const initializeExpanded = (cats: Category[]): { [code: string]: boolean } => {
            const expandedState: { [code: string]: boolean } = {};
            
            const processCategory = (cat: Category) => {
                if (cat.children && cat.children.length > 0) {
                    expandedState[cat.code] = true; // Default to expanded
                    cat.children.forEach(processCategory);
                }
            };
            
            cats.forEach(processCategory);
            return expandedState;
        };

        setExpanded(initializeExpanded(categories));
    }, [categories]);
    
    const [sort, setSort] = useState<{ field: string, direction: 'asc' | 'desc' }>({ field: '', direction: 'desc' });
    const [filter, setFilter] = useState<{ codes: string[]; name: string }>({ codes: [], name: '' });

    const allCodes = useMemo(() => {
        if (availableCodes && availableCodes.length > 0) return availableCodes;
        const codes: string[] = [];
        const flatten = (cats: Category[]) => cats.forEach(c => { codes.push(c.code); if (c.children) flatten(c.children); });
        flatten(categories);
        return codes.sort();
    }, [availableCodes, categories]);
    const codeRef = React.useRef<HTMLElement>(null);
    const nameRef = React.useRef<HTMLElement>(null);

	const handleToggle = (code: string) => {
		setExpanded(prev => ({ ...prev, [code]: !prev[code] }));
	};

    const renderCategoryRows = (
        cat: Category,
        level: number = 0,
        idx: number = 0,
        isEven: boolean = true,
        parent: { code: string, id: number } | null = null
    ): React.ReactNode[] => {
        const hasChildren = !!cat.children && cat.children.length > 0;
        const isOpen = expanded[cat.code] !== undefined ? expanded[cat.code] : hasChildren; // Default to expanded if has children
        const paddingLeft = 2 + level * 2;
        const bgColor = isEven ? 'var(--color-page-background)' : 'var(--color-container-background)';

        const rows: React.ReactNode[] = [
            <TableRow key={cat.code} sx={{ background: bgColor, height: 36 }}>
                <TableCell sx={{ pl: paddingLeft, width: 160, py: 0.5, border: 'none' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {hasChildren ? (
                            <IconButton size="small" sx={{ background: '#222222ff', '&:hover': { background: '#313131ff' }, p: 0.25 }} onClick={() => handleToggle(cat.code)}>
                                <div style={{ color: '#fff', width: 10, height: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ fontSize: 12 }}>{isOpen ? '−' : '+'}</span>
                                </div>
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 13.5, height: 13.5, borderRadius: '50%', background: 'inherit' }} />
                        )}
                        <Typography fontSize={14 - level}>{cat.code}</Typography>
                    </Box>
                </TableCell>
                <TableCell sx={{ width: 320, py: 0.5, border: 'none' }}>
                    <Typography fontSize={14 - level}>{cat.name}</Typography>
                </TableCell>
                <TableCell sx={{ width: 120, py: 0.5, textAlign: 'right', border: 'none' }}>
                    <Typography sx={{ color: Number(cat.balance) < 0 ? 'var(--color-negative-amount)' : 'inherit' }}>{formatAmount?.(Number(cat.balance))}</Typography>
                </TableCell>
                <TableCell sx={{ width: 80, py: 0.5, textAlign: 'right', border: 'none' }}>
                    <ActionMenu
                        actions={[
                            { label: 'Add Sub Category', onClick: () => onAddSubCategory?.({ code: cat.code, id: cat.id }), icon: 'add', disabled: !canCreate },
                            { label: 'Edit', onClick: () => onEditCategory?.({ id: cat.id, parent }), icon: 'edit', disabled: !canUpdate },
                            { label: 'Delete', onClick: () => onDeleteCategory?.(cat.id), icon: 'delete', disabled: !canDelete },
                        ]}
                    />
                </TableCell>
            </TableRow>
        ];
        if (hasChildren && isOpen) {
            cat.children!.forEach((child, i) => {
                const childIsEven = !isEven ? i % 2 === 0 : i % 2 !== 0;
                rows.push(...renderCategoryRows(child, level + 1, i, childIsEven, { code: cat.code, id: cat.id }));
            });
        }
        return rows;
    };

    // Mobile equivalent of renderCategoryRows above - same recursive
    // expand/collapse tree, rendered as indented cards instead of table rows,
    // following the same label-above-value stat block + flat zebra-striping
    // pattern established on Provisions/Transactions - `cardIndex` is a
    // shared running counter (not reset per level) so the striping alternates
    // across the whole flattened, currently-visible tree, not per-subtree.
    const renderCategoryCards = (
        cat: Category,
        level: number = 0,
        parent: { code: string, id: number } | null = null,
        cardIndex: { current: number } = { current: 0 },
    ): React.ReactNode[] => {
        const hasChildren = !!cat.children && cat.children.length > 0;
        const isOpen = expanded[cat.code] !== undefined ? expanded[cat.code] : hasChildren;
        const idx = cardIndex.current++;

        const cards: React.ReactNode[] = [
            <Box
                key={cat.code}
                sx={{
                    ml: level * 2,
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    background: idx % 2 !== 0 ? 'var(--color-container-background)' : 'var(--color-page-background)',
                    border: '1px solid #e0ecf7',
                    boxSizing: 'border-box',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        {hasChildren ? (
                            <IconButton size="small" sx={{ background: '#222222ff', '&:hover': { background: '#313131ff' }, p: 0.25, flexShrink: 0 }} onClick={() => handleToggle(cat.code)}>
                                <div style={{ color: '#fff', width: 10, height: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ fontSize: 12 }}>{isOpen ? '−' : '+'}</span>
                                </div>
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 13.5, height: 13.5, borderRadius: '50%', flexShrink: 0 }} />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#707070', textTransform: 'uppercase' }}>
                                {cat.code}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {cat.name}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: 'right', color: Number(cat.balance) < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)' }}>
                            {formatAmount?.(Number(cat.balance))}
                        </Typography>
                        <ActionMenu
                            actions={[
                                { label: 'Add Sub Category', onClick: () => onAddSubCategory?.({ code: cat.code, id: cat.id }), icon: 'add', disabled: !canCreate },
                                { label: 'Edit', onClick: () => onEditCategory?.({ id: cat.id, parent }), icon: 'edit', disabled: !canUpdate },
                                { label: 'Delete', onClick: () => onDeleteCategory?.(cat.id), icon: 'delete', disabled: !canDelete },
                            ]}
                        />
                    </Box>
                </Box>
            </Box>
        ];
        if (hasChildren && isOpen) {
            cat.children!.forEach(child => {
                cards.push(...renderCategoryCards(child, level + 1, { code: cat.code, id: cat.id }, cardIndex));
            });
        }
        return cards;
    };

    const clearCode = () => {
        setFilter(prev => ({ ...prev, codes: [] }));
        onFilter?.('code', []);
        if (sort.field === 'code') { setSort({ field: '', direction: 'desc' }); onSort?.('', 'desc'); }
    };
    const clearName = () => {
        setFilter(prev => ({ ...prev, name: '' }));
        onFilter?.('name', '');
        if (sort.field === 'name') { setSort({ field: '', direction: 'desc' }); onSort?.('', 'desc'); }
    };

    if (isMobile) {
        // Shared across every top-level category (and its children) so the
        // zebra stripe alternates continuously down the whole flattened,
        // currently-expanded tree instead of restarting at each root.
        const mobileCardIndex = { current: 0 };
        return (
            <Box sx={{ width: '100%' }}>
                {stage === 'complete' && categories.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {/* Sits outside the scrolling box below (same idea as
                            transactions-table-mobile.tsx's column header vs.
                            its react-window List) rather than position:sticky -
                            it never needs to "catch up" to a scroll position,
                            so there's no settling/jitter as it locks in. */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 0.5,
                                px: 1,
                                flexShrink: 0,
                                background: 'var(--color-page-background)',
                                borderBottom: '1px solid #e0e0e0',
                            }}
                        >
                            <Typography sx={{ fontSize: 10, fontWeight: 700, flex: 1, minWidth: 0, color: '#707070', textTransform: 'uppercase' }}>
                                Code / Name
                            </Typography>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#707070', textTransform: 'uppercase' }}>
                                Balance
                            </Typography>
                            <Box sx={{ width: 36, flexShrink: 0 }} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto', height: 'calc(100vh - 160px)', pt: 0.5 }}>
                            {categories.map(cat => renderCategoryCards(cat, 0, null, mobileCardIndex))}
                        </Box>
                    </Box>
                )}
                {stage === 'complete' && categories.length === 0 && (
                    <TablePlaceholder title="No Categories" message="No categories found. Try adjusting your filters or add a new category." />
                )}
                {stage !== 'complete' && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }} fontSize={16}>
                            {stage === 'initialize' ? 'Initializing categories...' : 'Loading Categories...'}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }

	return (
		<TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
			<Table stickyHeader>
                <CategoryNameCodeFilter
                    anchorEl={codeRef.current}
                    state={{
                        sortEnabled: sort.field === 'code',
                        filterEnabled: filter.codes.length > 0,
                        sortOrder: sort.direction,
                        search: '',
                        selectedCodes: filter.codes,
                    }}
                    availableCodes={allCodes}
                    open={isCodeFilterOpen}
                    for="code"
                    onApply={(opts) => {
                        if (opts.sortEnabled) { setSort({ field: 'code', direction: opts.sortOrder }); onSort?.('code', opts.sortOrder); }
                        const selected: string[] = opts.filterEnabled ? (opts.selectedCodes ?? []) : [];
                        setFilter(prev => ({ ...prev, codes: selected }));
                        onFilter?.('code', selected);
                        setIsCodeFilterOpen(false);
                    }}
                    onClear={clearCode}
                    onClose={() => setIsCodeFilterOpen(false)}/>
                <CategoryNameCodeFilter
                    anchorEl={nameRef.current}
                    state={{
                        sortEnabled: sort.field === 'name',
                        filterEnabled: !!filter.name,
                        sortOrder: sort.direction,
                        search: filter.name,
                    }}
                    open={isNameFilterOpen}
                    for="name"
                    onApply={(opts) => {
                        if (opts.sortEnabled) { setSort({ field: 'name', direction: opts.sortOrder }); onSort?.('name', opts.sortOrder); }
                        setFilter(prev => ({ ...prev, name: opts.filterEnabled ? opts.search : '' }));
                        onFilter?.('name', opts.filterEnabled ? opts.search : '');
                        setIsNameFilterOpen(false);
                    }}
                    onClear={clearName}
                    onClose={() => setIsNameFilterOpen(false)}/>
				<TableHead>
					<TableRow>
						<TableCell 
                            ref={codeRef} 
                            onClick={() => setIsCodeFilterOpen(!isCodeFilterOpen)} 
                            sx={{ fontWeight: 600, fontSize: 15, border: 'none', background: 'var(--color-container-background)', borderRadius: '8px 0 0 8px' }}
                        >
                            Category Code <span style={{ color: 'var(--color-primary)' }}>▼</span>
                        </TableCell>

						<TableCell 
                            ref={nameRef} 
                            onClick={() => setIsNameFilterOpen(!isNameFilterOpen)} 
                            sx={{ fontWeight: 600, fontSize: 15, border: 'none', background: 'var(--color-container-background)' }}
                        >
                            Category Name <span style={{ color: 'var(--color-primary)' }}>▼</span>
                        </TableCell>

						<TableCell 
                            sx={{ fontWeight: 600, fontSize: 15, border: 'none', background: 'var(--color-container-background)', textAlign: 'center' }}
                            onClick={() => {
                                const newDirection = sort.field === 'balance' && sort.direction === 'asc' ? 'desc' : 'asc';
                                setSort({ field: 'balance', direction: newDirection });
                                onSort?.('balance', newDirection);
                            }}
                        >
                            Balance <span style={{ color: sort.field === 'balance' ? 'var(--color-primary)' : 'rgba(var(--color-primary-rgb), 0.2667)' }}>{sort.direction === 'asc' ? '▲' : '▼'}</span>
                        </TableCell>

						<TableCell 
                            sx={{ fontWeight: 600, fontSize: 15, border: 'none', background: 'var(--color-container-background)', textAlign: 'right', borderRadius: '0 8px 8px 0' }}
                        >
                            Action
                        </TableCell>
					</TableRow>
				</TableHead>
                { stage === 'complete' && categories.length > 0 &&
                    <TableBody>
                        {categories.map((cat, i) => renderCategoryRows(cat, 0, i, (i % 2 === 0)))}
                    </TableBody>
                }
			</Table>
            {stage === 'complete' && categories.length === 0 && (
                <TablePlaceholder title="No Categories" message="No categories found. Try adjusting your filters or add a new category." />
            )}
            {stage !== 'complete' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }} fontSize={16}>
                        {stage === 'initialize' ? 'Initializing categories...' : 'Loading Categories...'}
                    </Typography>
                </Box>
            )}
		</TableContainer>
	);
};

export default CategoryTable;
